import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Task } from '../../types/task';
import { Button } from '../ui/Button';
import { Edit2, X, Save } from 'lucide-react';
import { useStore } from '../../store/useStore';
import toast from 'react-hot-toast';

interface TaskEditModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TaskEditModal: React.FC<TaskEditModalProps> = ({ isOpen, onClose }) => {
  const { currentUser, tasks, updateTask } = useStore();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');

  // Get tasks created by current user
  const userTasks = tasks.filter(task => 
    task.assignedBy === currentUser?.id && task.status !== 'COMPLETED'
  );

  const handleTaskSelect = (task: Task) => {
    setSelectedTask(task);
    setEditedTitle(task.title);
    setEditedDescription(task.description);
  };

  const handleSave = () => {
    if (!selectedTask) return;

    updateTask(selectedTask.id, {
      title: editedTitle,
      description: editedDescription,
      updatedAt: new Date()
    });

    toast.success('Task updated successfully');
    setSelectedTask(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-2xl bg-white rounded-xl shadow-xl"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Edit Tasks</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              icon={<X className="w-4 h-4" />}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Task List */}
            <div className="border-r pr-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Select a task to edit</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {userTasks.map(task => (
                  <button
                    key={task.id}
                    onClick={() => handleTaskSelect(task)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedTask?.id === task.id
                        ? 'bg-emerald-50 border-2 border-emerald-500'
                        : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                    }`}
                  >
                    <h4 className="font-medium text-gray-900">{task.title}</h4>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{task.description}</p>
                  </button>
                ))}
                {userTasks.length === 0 && (
                  <p className="text-center text-gray-500 py-4">No tasks available to edit</p>
                )}
              </div>
            </div>

            {/* Edit Form */}
            <div>
              {selectedTask ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      rows={5}
                      className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    icon={<Save className="w-4 h-4" />}
                    className="w-full"
                  >
                    Save Changes
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <p>Select a task to edit</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};